import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  LinearProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  School,
  PlayArrow,
  CheckCircle,
  SkipNext,
  Help,
  TrendingUp,
  AutoAwesome,
  Psychology,
  SmartToy,
  ContentPaste,
  Campaign,
  Support,
  Analytics,
  Settings,
  ExpandMore,
  Close,
  Refresh,
  Book,
  VideoLibrary,
  Quiz
} from '@mui/icons-material';

const TrainingSystem = () => {
  const [showWelcome, setShowWelcome] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [userProgress, setUserProgress] = useState({
    onboardingCompleted: false,
    featuresUsed: [],
    trainingSessions: 0,
    skillLevel: 'beginner',
    lastTrainingDate: null,
    totalPoints: 0,
    badges: []
  });
  const [suggestedTraining, setSuggestedTraining] = useState([]);
  const [showTrainingDialog, setShowTrainingDialog] = useState(false);
  const [activeTraining, setActiveTraining] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [sectionProgress, setSectionProgress] = useState({});
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  useEffect(() => {
    // Check if user is new (first time)
    const isNewUser = !localStorage.getItem('trainingCompleted');
    if (isNewUser) {
      setShowWelcome(true);
    }
    
    // Load user progress
    loadUserProgress();
    
    // Generate suggested training based on user behavior
    generateSuggestedTraining();
  }, []);

  const loadUserProgress = () => {
    const savedProgress = localStorage.getItem('userProgress');
    if (savedProgress) {
      setUserProgress(JSON.parse(savedProgress));
    }
  };

  const saveUserProgress = (progress) => {
    const updatedProgress = { ...userProgress, ...progress };
    setUserProgress(updatedProgress);
    localStorage.setItem('userProgress', JSON.stringify(updatedProgress));
  };

  const generateSuggestedTraining = () => {
    // Analyze user behavior and generate personalized training suggestions
    const suggestions = [];
    
    if (!userProgress.featuresUsed.includes('ai-content-generator')) {
      suggestions.push({
        id: 'ai-content-generator',
        title: 'AI Content Generator Mastery',
        description: 'Master AI-powered content creation and direct posting',
        priority: 'high',
        estimatedTime: '25 min',
        icon: <AutoAwesome />
      });
    }
    
    if (!userProgress.featuresUsed.includes('content')) {
      suggestions.push({
        id: 'content-creation',
        title: 'Content Creation Mastery',
        description: 'Learn how to create viral posts and engaging content',
        priority: 'high',
        estimatedTime: '15 min',
        icon: <ContentPaste />
      });
    }
    
    if (!userProgress.featuresUsed.includes('ads')) {
      suggestions.push({
        id: 'ad-campaigns',
        title: 'Ad Campaign Optimization',
        description: 'Master Facebook and Instagram ad campaigns',
        priority: 'high',
        estimatedTime: '20 min',
        icon: <Campaign />
      });
    }
    
    if (!userProgress.featuresUsed.includes('analytics')) {
      suggestions.push({
        id: 'analytics-insights',
        title: 'Analytics & Performance',
        description: 'Understand your social media performance metrics',
        priority: 'medium',
        estimatedTime: '12 min',
        icon: <Analytics />
      });
    }
    
    setSuggestedTraining(suggestions);
  };

  const welcomeSteps = [
    {
      label: 'Welcome to Your AI Social Media Manager',
      content: {
        title: '🎉 Welcome to Your AI-Powered Social Media Suite!',
        description: 'I\'m your personal AI assistant, ready to help you master social media management. Let me show you everything I can do for your business.',
        features: [
          '🤖 AI-Powered Content Creation',
          '📤 Direct Posting to Social Media',
          '💰 Ad Campaign Creation',
          '📊 Smart Ad Campaign Management',
          '🔧 Automated Tech Support',
          '💬 Intelligent Customer Service',
          '📈 Advanced Analytics & Insights'
        ]
      }
    },
    {
      label: 'Your AI Agent - Multi-Role Assistant',
      content: {
        title: 'Meet Your Virtual Social Media Team',
        description: 'I act as 5 different specialists to handle all your social media needs:',
        roles: [
          {
            title: 'AI Content Creator',
            description: 'Generates viral posts, captions, hashtags, and posts directly to social media',
            icon: <ContentPaste />
          },
          {
            title: 'Ad Campaign Manager',
            description: 'Creates and optimizes Facebook/Instagram ads from AI-generated content',
            icon: <Campaign />
          },
          {
            title: 'Tech Support Specialist',
            description: 'Fixes platform issues and integrations',
            icon: <Support />
          },
          {
            title: 'Customer Support Manager',
            description: 'Handles inquiries and community engagement',
            icon: <Help />
          },
          {
            title: 'Analytics Specialist',
            description: 'Tracks performance and generates insights',
            icon: <Analytics />
          }
        ]
      }
    },
    {
      label: 'Key Features Overview',
      content: {
        title: '🚀 Powerful Features at Your Fingertips',
        description: 'Here\'s what you can do with our platform:',
        features: [
          {
            title: 'AI Content Generator',
            description: 'Create platform-optimized content with direct posting capabilities',
            icon: <AutoAwesome />
          },
          {
            title: 'Direct Social Media Posting',
            description: 'Post to multiple platforms instantly from AI-generated content',
            icon: <ContentPaste />
          },
          {
            title: 'Ad Campaign Creation',
            description: 'Transform AI content into powerful paid campaigns',
            icon: <Campaign />
          },
          {
            title: 'AI-Powered Notifications',
            description: 'Smart reminders when you haven\'t posted',
            icon: <SmartToy />
          },
          {
            title: 'Comprehensive Analytics',
            description: 'Track performance across all platforms',
            icon: <TrendingUp />
          },
          {
            title: 'Multi-Platform Management',
            description: 'Manage Facebook, Instagram, Twitter, LinkedIn',
            icon: <Psychology />
          }
        ]
      }
    },
    {
      label: 'Getting Started',
      content: {
        title: '🎯 Ready to Get Started?',
        description: 'I\'ll guide you through each feature and help you maximize your social media success.',
        nextSteps: [
          'Connect your social media accounts',
          'Try the AI Content Generator',
          'Post directly to social media',
          'Create your first ad campaign',
          'Explore analytics and insights',
          'Configure notification preferences'
        ]
      }
    }
  ];

  const trainingModules = [
    {
      id: 'ai-content-generator',
      title: 'AI Content Generator Mastery',
      description: 'Master the AI-powered content creation and direct posting features',
      duration: '25 min',
      difficulty: 'Beginner',
      sections: [
        {
          title: 'Understanding Your AI Content Generator',
          content: 'Your AI Content Generator creates platform-optimized content that drives engagement and conversions.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'AI Content Generator Overview',
          steps: [
            'Navigate to Content Hub → AI Content Generator tab',
            'AI analyzes your industry, audience, and platform preferences',
            'Generates content optimized for each social media platform',
            'Provides hashtag suggestions and optimal posting times',
            'Creates engaging content that matches your brand voice'
          ],
          quiz: [
            {
              question: 'What does the AI consider when generating content?',
              options: ['Only your brand name', 'Your industry, audience, platform, and brand voice', 'Just trending topics', 'Random content'],
              correct: 1,
              explanation: 'The AI analyzes your industry, target audience, chosen platform, and brand voice to create relevant, engaging content.'
            }
          ]
        },
        {
          title: 'Creating Your First AI-Generated Post',
          content: 'Step-by-step guide to using the AI Content Generator for maximum impact.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Creating AI-Generated Content',
          steps: [
            'Select your target platform (Instagram, Facebook, Twitter, LinkedIn)',
            'Choose your content tone (Professional, Casual, Energetic, Educational, Humorous)',
            'Enter your brand/company name and industry',
            'Select content length (Short, Medium, Long)',
            'Describe your topic or message in detail',
            'Click "Generate AI Content" and review the results'
          ],
          quiz: [
            {
              question: 'What\'s the first step in creating AI-generated content?',
              options: ['Enter your topic', 'Select your platform', 'Choose your tone', 'Set your budget'],
              correct: 1,
              explanation: 'Start by selecting your target platform so the AI can optimize content specifically for that platform.'
            }
          ]
        },
        {
          title: 'Direct Posting from AI Generator',
          content: 'Learn how to post your AI-generated content directly to social media platforms.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Direct Posting Guide',
          steps: [
            'After generating content, click "Post Now" button',
            'Select which platforms to post to (Instagram, Facebook, Twitter, LinkedIn)',
            'Choose to post immediately or schedule for later',
            'Add images or videos to enhance your post',
            'Review your post preview and confirm',
            'Track your post performance in real-time'
          ],
          quiz: [
            {
              question: 'How many platforms can you post to simultaneously?',
              options: ['Only one', 'Two platforms', 'All connected platforms', 'Depends on your plan'],
              correct: 2,
              explanation: 'You can post to all your connected platforms simultaneously for maximum reach and efficiency.'
            }
          ]
        },
        {
          title: 'Scheduling Posts for Optimal Timing',
          content: 'Learn how to schedule your AI-generated content for the best engagement times.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Post Scheduling Strategy',
          steps: [
            'Use the "Schedule Post" option instead of "Post Now"',
            'Select your preferred posting date and time',
            'Choose multiple platforms for cross-posting',
            'Set up recurring posts for consistent content',
            'Use AI-suggested optimal times for each platform',
            'Monitor scheduled posts in your content calendar'
          ],
          quiz: [
            {
              question: 'What\'s the benefit of scheduling posts?',
              options: ['Save time', 'Post at optimal times', 'Maintain consistency', 'All of the above'],
              correct: 3,
              explanation: 'Scheduling saves time, ensures posting at optimal times, and helps maintain consistent content flow.'
            }
          ]
        },
        {
          title: 'Creating Ad Campaigns from AI Content',
          content: 'Transform your AI-generated content into powerful ad campaigns.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Ad Campaign Creation',
          steps: [
            'After generating content, click "Create Ad Campaign"',
            'Set your campaign budget (recommended: $25-100/day)',
            'Choose campaign duration (7-30 days)',
            'Select target audience (General, Business, Entrepreneurs, Marketers)',
            'Pick campaign objective (Engagement, Traffic, Conversions, Awareness)',
            'Review campaign settings and launch'
          ],
          quiz: [
            {
              question: 'What\'s the recommended starting budget for ad campaigns?',
              options: ['$5/day', '$25/day', '$100/day', '$500/day'],
              correct: 1,
              explanation: '$25/day allows enough data collection while keeping costs manageable for testing and optimization.'
            }
          ]
        },
        {
          title: 'AI Insights and Performance Predictions',
          content: 'Understand the AI insights and predictions for your content performance.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'AI Insights Analysis',
          steps: [
            'Review engagement predictions (High/Medium/Low)',
            'Check AI-suggested optimal posting times',
            'Read audience insights and recommendations',
            'Use content recommendations to improve future posts',
            'Track actual performance vs. AI predictions',
            'Learn from AI insights to refine your strategy'
          ],
          quiz: [
            {
              question: 'What do AI insights help you understand?',
              options: ['Only engagement rates', 'Engagement, timing, audience, and recommendations', 'Just posting times', 'Only hashtags'],
              correct: 1,
              explanation: 'AI insights provide comprehensive guidance including engagement predictions, optimal timing, audience insights, and content recommendations.'
            }
          ]
        },
        {
          title: 'Advanced AI Content Strategies',
          content: 'Master advanced techniques for maximizing your AI content performance.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Advanced AI Strategies',
          steps: [
            'Experiment with different tones for different platforms',
            'Use AI-generated hashtags to increase discoverability',
            'Combine AI content with your own creative elements',
            'Test different content lengths for each platform',
            'Use AI insights to refine your brand voice',
            'Create content series using AI-generated themes'
          ],
          quiz: [
            {
              question: 'How can you improve AI-generated content?',
              options: ['Use it exactly as generated', 'Add your own creative elements', 'Ignore AI suggestions', 'Only use manual content'],
              correct: 1,
              explanation: 'The best approach is to use AI-generated content as a foundation and add your own creative elements for authenticity.'
            }
          ]
        }
      ]
    },
    {
      id: 'content-creation',
      title: 'Content Creation Mastery',
      description: 'Learn how to create viral posts and engaging content',
      duration: '15 min',
      difficulty: 'Beginner',
      sections: [
        {
          title: 'Understanding Your AI Content Creator',
          content: 'Your AI analyzes trending topics, your industry, and your audience to generate engaging content.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'How AI Creates Viral Content',
          steps: [
            'AI analyzes your industry trends and audience preferences',
            'Generates content that matches your brand voice',
            'Optimizes for maximum engagement and reach',
            'Suggests best posting times for your audience'
          ],
          quiz: [
            {
              question: 'What does the AI consider when creating content?',
              options: ['Only trending hashtags', 'Your industry, audience, and trends', 'Just your previous posts', 'Random topics'],
              correct: 1,
              explanation: 'The AI analyzes your industry, target audience, current trends, and your brand voice to create relevant content.'
            }
          ]
        },
        {
          title: 'Creating Your First AI-Generated Post',
          content: 'Step-by-step guide to creating posts that drive engagement and reach.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Creating Your First Post',
          steps: [
            'Navigate to Content → Create Post',
            'Enter your business description and target audience',
            'Choose your content type (promotional, educational, entertaining)',
            'Review AI-generated content and customize if needed',
            'Add images or videos to enhance engagement',
            'Schedule your post for optimal timing'
          ],
          quiz: [
            {
              question: 'What\'s the best time to post for maximum engagement?',
              options: ['Early morning', '2-4 PM', 'Late night', 'Any time'],
              correct: 1,
              explanation: 'Studies show 2-4 PM gets the highest engagement as people are on lunch breaks or finishing work.'
            }
          ]
        },
        {
          title: 'Optimizing Hashtags for Reach',
          content: 'Learn how to use trending and relevant hashtags to expand your reach.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Hashtag Strategy',
          steps: [
            'Use 5-10 relevant hashtags per post',
            'Mix popular and niche hashtags',
            'Research trending hashtags in your industry',
            'Create branded hashtags for your business',
            'Monitor hashtag performance and adjust strategy'
          ],
          quiz: [
            {
              question: 'How many hashtags should you use per post?',
              options: ['1-2', '5-10', '15-20', 'As many as possible'],
              correct: 1,
              explanation: '5-10 hashtags is the sweet spot - enough to reach new audiences without looking spammy.'
            }
          ]
        },
        {
          title: 'Content Calendar Strategy',
          content: 'Plan and schedule your content for consistent posting and maximum impact.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Content Planning',
          steps: [
            'Plan content themes for each day of the week',
            'Mix promotional, educational, and entertaining content',
            'Schedule posts in advance using the calendar feature',
            'Use AI insights to optimize posting times',
            'Track performance and adjust your strategy'
          ],
          quiz: [
            {
              question: 'What\'s the ideal content mix for social media?',
              options: ['80% promotional, 20% other', '40% educational, 30% entertaining, 30% promotional', 'All promotional content', 'Random mix'],
              correct: 1,
              explanation: 'The 40-30-30 rule provides value to your audience while maintaining promotional goals.'
            }
          ]
        }
      ]
    },
    {
      id: 'ad-campaigns',
      title: 'Ad Campaign Optimization',
      description: 'Master Facebook and Instagram ad campaigns',
      duration: '20 min',
      difficulty: 'Intermediate',
      sections: [
        {
          title: 'Understanding Auto-Ad Integration',
          content: 'Learn how every post can automatically become an optimized ad campaign.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Auto-Ad System Overview',
          steps: [
            'Enable auto-ad in your post settings',
            'AI analyzes post performance and engagement',
            'Automatically creates optimized ad campaigns',
            'Targets similar audiences who engaged with your content',
            'Scales successful posts into full campaigns'
          ],
          quiz: [
            {
              question: 'What happens when you enable auto-ad?',
              options: ['All posts become ads automatically', 'You choose which posts become ads', 'Only viral posts become ads', 'Nothing changes'],
              correct: 1,
              explanation: 'You maintain control - the AI suggests which posts to turn into ads based on performance.'
            }
          ]
        },
        {
          title: 'Setting Up Your First Campaign',
          content: 'Step-by-step guide to creating effective ad campaigns with AI optimization.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Campaign Setup Guide',
          steps: [
            'Choose your campaign objective (awareness, consideration, conversion)',
            'Set your budget and bidding strategy',
            'Define your target audience demographics',
            'Create compelling ad copy and visuals',
            'Set up conversion tracking',
            'Launch and monitor performance'
          ],
          quiz: [
            {
              question: 'What\'s the recommended starting budget for a new campaign?',
              options: ['$5/day', '$25/day', '$100/day', '$500/day'],
              correct: 1,
              explanation: '$25/day allows enough data collection while keeping costs manageable for testing.'
            }
          ]
        },
        {
          title: 'Analyzing Campaign Performance',
          content: 'Learn how to read analytics and optimize your campaigns for better ROI.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Performance Analysis',
          steps: [
            'Monitor key metrics: CTR, CPC, CPM, conversion rate',
            'Identify top-performing ad creatives and audiences',
            'A/B test different ad variations',
            'Optimize targeting based on performance data',
            'Scale successful campaigns and pause underperformers'
          ],
          quiz: [
            {
              question: 'What\'s a good click-through rate (CTR)?',
              options: ['1-2%', '5-8%', '15-20%', 'Above 50%'],
              correct: 1,
              explanation: '5-8% CTR is considered good for most industries, though benchmarks vary by niche.'
            }
          ]
        },
        {
          title: 'Advanced Targeting Strategies',
          content: 'Master audience targeting to reach your ideal customers.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Audience Targeting',
          steps: [
            'Use custom audiences from your email list',
            'Create lookalike audiences based on your best customers',
            'Target by interests, behaviors, and demographics',
            'Exclude audiences who already converted',
            'Test different audience segments systematically'
          ],
          quiz: [
            {
              question: 'What\'s the best way to find new customers?',
              options: ['Target everyone', 'Use lookalike audiences', 'Only target existing followers', 'Random targeting'],
              correct: 1,
              explanation: 'Lookalike audiences help you reach people similar to your best existing customers.'
            }
          ]
        }
      ]
    },
    {
      id: 'analytics-insights',
      title: 'Analytics & Performance',
      description: 'Understand your social media performance metrics',
      duration: '12 min',
      difficulty: 'Beginner',
      sections: [
        {
          title: 'Understanding Your Dashboard',
          content: 'Learn how to read and interpret your social media performance metrics.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Dashboard Overview',
          steps: [
            'Navigate to Analytics → Overview',
            'Review key metrics: reach, engagement, clicks',
            'Compare performance across different time periods',
            'Identify your best-performing content types',
            'Track follower growth and audience insights'
          ],
          quiz: [
            {
              question: 'What does engagement rate measure?',
              options: ['Only likes', 'Likes, comments, shares, and saves', 'Only comments', 'Only shares'],
              correct: 1,
              explanation: 'Engagement rate includes all interactions: likes, comments, shares, saves, and clicks.'
            }
          ]
        },
        {
          title: 'Key Performance Indicators',
          content: 'Identify the most important metrics for your business goals.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'KPI Analysis',
          steps: [
            'For brand awareness: focus on reach and impressions',
            'For engagement: track likes, comments, shares',
            'For conversions: monitor click-through rates and sales',
            'For audience growth: track follower acquisition',
            'Set benchmarks based on your industry'
          ],
          quiz: [
            {
              question: 'Which metric is most important for brand awareness?',
              options: ['Engagement rate', 'Reach and impressions', 'Click-through rate', 'Conversion rate'],
              correct: 1,
              explanation: 'Reach and impressions show how many people see your content, crucial for brand awareness.'
            }
          ]
        },
        {
          title: 'Using Insights to Improve',
          content: 'Learn how to use analytics data to optimize your content strategy.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Data-Driven Strategy',
          steps: [
            'Analyze which content types perform best',
            'Identify optimal posting times for your audience',
            'Track hashtag performance and engagement',
            'Monitor audience demographics and interests',
            'Use insights to refine your content calendar'
          ],
          quiz: [
            {
              question: 'How often should you review your analytics?',
              options: ['Once a month', 'Weekly', 'Daily', 'Only when campaigns end'],
              correct: 1,
              explanation: 'Weekly reviews help you stay on top of trends and make timely adjustments.'
            }
          ]
        },
        {
          title: 'Competitor Analysis',
          content: 'Learn how to analyze competitor performance and identify opportunities.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Competitor Research',
          steps: [
            'Identify your main competitors in the space',
            'Analyze their content strategy and posting frequency',
            'Study their best-performing content types',
            'Monitor their hashtag usage and engagement',
            'Identify gaps and opportunities in your strategy'
          ],
          quiz: [
            {
              question: 'What\'s the best way to learn from competitors?',
              options: ['Copy their content exactly', 'Analyze their strategy and adapt', 'Ignore them completely', 'Only focus on their mistakes'],
              correct: 1,
              explanation: 'Analyze their successful strategies and adapt them to your unique brand and audience.'
            }
          ]
        }
      ]
    },
    {
      id: 'platform-integration',
      title: 'Platform Integration Mastery',
      description: 'Connect and manage all your social media accounts',
      duration: '10 min',
      difficulty: 'Beginner',
      sections: [
        {
          title: 'Connecting Your Social Media Accounts',
          content: 'Learn how to securely connect your social media platforms for seamless management.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Account Connection',
          steps: [
            'Navigate to Settings → Platform Connections',
            'Click "Connect Account" for each platform',
            'Grant necessary permissions for posting and analytics',
            'Verify connection by checking account status',
            'Repeat for all platforms you want to manage'
          ],
          quiz: [
            {
              question: 'What permissions do you need to grant?',
              options: ['None', 'Posting and analytics access', 'Full account access', 'Only read access'],
              correct: 1,
              explanation: 'Posting and analytics permissions allow the platform to schedule posts and track performance.'
            }
          ]
        },
        {
          title: 'Managing Multiple Accounts',
          content: 'Learn how to efficiently manage multiple social media accounts from one dashboard.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Multi-Account Management',
          steps: [
            'Add all your business accounts to the platform',
            'Organize accounts by business or client',
            'Set default accounts for each platform',
            'Use bulk actions for efficient management',
            'Monitor all accounts from unified dashboard'
          ],
          quiz: [
            {
              question: 'How many accounts can you manage?',
              options: ['1 per platform', '5 per platform', 'Unlimited', 'Depends on your plan'],
              correct: 2,
              explanation: 'You can manage unlimited accounts, perfect for agencies and multi-business owners.'
            }
          ]
        },
        {
          title: 'Security Best Practices',
          content: 'Keep your social media accounts secure while using third-party tools.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Account Security',
          steps: [
            'Use strong, unique passwords for each account',
            'Enable two-factor authentication',
            'Regularly review connected apps and permissions',
            'Monitor account activity for suspicious behavior',
            'Keep your access tokens secure and updated'
          ],
          quiz: [
            {
              question: 'What\'s the most important security measure?',
              options: ['Strong passwords', 'Two-factor authentication', 'Regular monitoring', 'All of the above'],
              correct: 3,
              explanation: 'Security requires multiple layers: strong passwords, 2FA, and regular monitoring.'
            }
          ]
        }
      ]
    },
    {
      id: 'notification-system',
      title: 'Smart Notifications',
      description: 'Configure AI-powered notification system',
      duration: '8 min',
      difficulty: 'Beginner',
      sections: [
        {
          title: 'Setting Up Smart Notifications',
          content: 'Configure intelligent notifications to stay on top of your social media game.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Notification Setup',
          steps: [
            'Navigate to Settings → Notifications',
            'Choose notification types: email, SMS, in-app',
            'Set inactive thresholds based on your posting frequency',
            'Configure notification timing and frequency',
            'Test your notification settings'
          ],
          quiz: [
            {
              question: 'What triggers inactive notifications?',
              options: ['Random alerts', 'When you haven\'t posted in X days', 'Only on weekends', 'Never'],
              correct: 1,
              explanation: 'Inactive notifications alert you when you haven\'t posted for your configured threshold.'
            }
          ]
        },
        {
          title: 'Customizing Notification Preferences',
          content: 'Personalize your notification experience for maximum effectiveness.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Customization Guide',
          steps: [
            'Set different thresholds for different platforms',
            'Choose notification times that work for you',
            'Configure urgent vs. regular notifications',
            'Set up team notifications for collaboration',
            'Review and adjust based on your workflow'
          ],
          quiz: [
            {
              question: 'How often should you review notification settings?',
              options: ['Never', 'Once a year', 'Monthly', 'When your workflow changes'],
              correct: 3,
              explanation: 'Review settings when your posting schedule or workflow changes to maintain effectiveness.'
            }
          ]
        }
      ]
    },
    {
      id: 'lead-capture-system',
      title: 'Advanced Lead Generation & Qualification',
      description: 'Master industry-specific lead capture and automatic qualification',
      duration: '25 min',
      difficulty: 'Advanced',
      sections: [
        {
          title: 'Understanding Advanced Lead Generation',
          content: 'Our platform automatically detects and qualifies leads from your social media engagement using industry-specific keywords and intent scoring.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Advanced Lead Generation Overview',
          steps: [
            'Navigate to Lead Generation section in your dashboard',
            'Understand automatic lead detection from social media posts',
            'Learn about industry-specific keyword qualification',
            'Review the intent scoring system (High/Medium/Low)',
            'Explore direct contact integration capabilities'
          ],
          quiz: [
            {
              question: 'How does our lead generation system work?',
              options: ['Manual lead entry only', 'Automatic detection from social media engagement with industry keywords', 'Only from paid ads', 'Only from website forms'],
              correct: 1,
              explanation: 'Our system automatically scans your social media posts for engagement and uses industry-specific keywords to identify qualified leads.'
            }
          ]
        },
        {
          title: 'Industry-Specific Lead Qualification',
          content: 'Set up industry-specific keywords and scoring for automatic lead qualification.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Industry Setup Guide',
          steps: [
            'Select your industry from pre-configured templates',
            'Review industry-specific keywords (real estate, plumbing, healthcare, etc.)',
            'Customize keywords for your specific business niche',
            'Configure intent scoring thresholds',
            'Test lead detection with sample content'
          ],
          quiz: [
            {
              question: 'Which industries are supported for automatic lead qualification?',
              options: ['Only real estate', 'Real estate, plumbing, healthcare, fitness, attorney, and more', 'Only service businesses', 'Only online businesses'],
              correct: 1,
              explanation: 'Our platform supports multiple industries including real estate, plumbing, healthcare, fitness, attorney, and many others with pre-configured keywords.'
            }
          ]
        },
        {
          title: 'Intent Scoring System',
          content: 'Master the advanced intent scoring system that categorizes leads based on engagement behavior.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Intent Scoring Mastery',
          steps: [
            'Understand High Intent leads (25-30 points) with buying keywords',
            'Identify Medium Intent leads (15 points) with shares and multiple interactions',
            'Recognize Low Intent leads (5 points) with likes and general engagement',
            'Configure custom scoring thresholds for your business',
            'Set up alerts for high-intent lead detection'
          ],
          quiz: [
            {
              question: 'What determines a High Intent lead?',
              options: ['Any engagement', 'Comments with buying keywords (25-30 points)', 'Only likes', 'Only follows'],
              correct: 1,
              explanation: 'High Intent leads are identified by comments containing buying keywords and receive 25-30 points in our scoring system.'
            }
          ]
        },
        {
          title: 'Email & SMS Integration',
          content: 'Connect your email and SMS services to contact leads directly from the platform.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Integration Setup',
          steps: [
            'Connect Gmail, Outlook, or SMTP for email integration',
            'Set up Twilio, MessageBird, or Vonage for SMS',
            'Configure OAuth authentication for secure connections',
            'Test email and SMS delivery with sample messages',
            'Set up industry-specific message templates'
          ],
          quiz: [
            {
              question: 'What email services can you integrate with?',
              options: ['Only Gmail', 'Gmail, Outlook, SMTP, and custom email services', 'Only business email', 'Only personal email'],
              correct: 1,
              explanation: 'You can integrate with Gmail, Outlook, SMTP, and any custom email service that supports SMTP for secure lead communication.'
            }
          ]
        },
        {
          title: 'Industry Templates & Direct Contact',
          content: 'Use pre-written industry templates to contact leads immediately with personalized messages.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Template Management',
          steps: [
            'Browse industry-specific email and SMS templates',
            'Customize templates with your business information',
            'Use live preview to see exactly how messages will appear',
            'Send personalized messages directly from the platform',
            'Track message opens, clicks, and responses'
          ],
          quiz: [
            {
              question: 'What are industry templates?',
              options: ['Generic messages', 'Pre-written, industry-specific email and SMS templates for immediate lead contact', 'Only email templates', 'Only for large businesses'],
              correct: 1,
              explanation: 'Industry templates are pre-written, industry-specific email and SMS templates designed for immediate lead contact with personalization options.'
            }
          ]
        },
        {
          title: 'Lead Management & Follow-up',
          content: 'Effectively manage and follow up with qualified leads using advanced tracking and automation.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Lead Management',
          steps: [
            'Review qualified leads in your lead dashboard',
            'Filter leads by intent level and industry',
            'Send personalized follow-up messages',
            'Track lead progression and engagement',
            'Set up automated follow-up sequences'
          ],
          quiz: [
            {
              question: 'How can you manage qualified leads?',
              options: ['Only manually', 'Filter by intent level, send personalized messages, track progression, and automate follow-ups', 'Only through email', 'Only through phone calls'],
              correct: 1,
              explanation: 'You can filter leads by intent level, send personalized messages, track progression, and set up automated follow-up sequences for comprehensive lead management.'
            }
          ]
        }
      ]
    },
    {
      id: 'real-posting-system',
      title: 'Real Posting & Social Integration',
      description: 'Master direct social media posting and platform integration',
      duration: '20 min',
      difficulty: 'Intermediate',
      sections: [
        {
          title: 'Connecting Social Media Accounts',
          content: 'Learn how to securely connect your social media platforms for direct posting.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Account Connection',
          steps: [
            'Navigate to Platform Connector in settings',
            'Click "Connect Account" for each platform',
            'Grant necessary permissions for posting',
            'Verify connection by checking account status',
            'Test posting functionality with a draft'
          ],
          quiz: [
            {
              question: 'What permissions do you need for direct posting?',
              options: ['None', 'Posting permissions only', 'Full account access', 'Only read access'],
              correct: 1,
              explanation: 'You need posting permissions to schedule and publish content directly to your social media accounts.'
            }
          ]
        },
        {
          title: 'Direct Posting Features',
          content: 'Master the direct posting capabilities across all connected platforms.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Direct Posting Guide',
          steps: [
            'Create content in the AI Content Generator',
            'Select target platforms for posting',
            'Add images, videos, or links to your posts',
            'Choose posting time (immediate or scheduled)',
            'Review post preview and confirm publication',
            'Track post performance in real-time'
          ],
          quiz: [
            {
              question: 'Can you post to multiple platforms simultaneously?',
              options: ['No, only one at a time', 'Yes, all connected platforms', 'Only Facebook and Instagram', 'Depends on your plan'],
              correct: 1,
              explanation: 'You can post to all your connected platforms simultaneously for maximum reach and efficiency.'
            }
          ]
        },
        {
          title: 'Advanced Posting Strategies',
          content: 'Learn advanced techniques for maximizing your social media reach.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Advanced Strategies',
          steps: [
            'Use platform-specific content optimization',
            'Schedule posts for optimal engagement times',
            'Create cross-platform content campaigns',
            'Use hashtags strategically for each platform',
            'Monitor and respond to engagement quickly',
            'A/B test different posting strategies'
          ],
          quiz: [
            {
              question: 'What\'s the best time to post for maximum engagement?',
              options: ['Early morning', '2-4 PM', 'Late night', 'Any time'],
              correct: 1,
              explanation: '2-4 PM typically gets the highest engagement as people are on lunch breaks or finishing work.'
            }
          ]
        }
      ]
    },
    {
      id: 'ai-agent-dashboard',
      title: 'AI Agent Dashboard Mastery',
      description: 'Master your AI agent\'s multi-role capabilities',
      duration: '18 min',
      difficulty: 'Advanced',
      sections: [
        {
          title: 'Understanding Your AI Agent',
          content: 'Your AI agent acts as 5 different specialists to handle all your social media needs.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'AI Agent Overview',
          steps: [
            'Navigate to AI Agent Dashboard',
            'Understand the 5 AI roles: Content Creator, Ad Manager, Tech Support, Customer Support, Analytics',
            'Learn how each role specializes in different tasks',
            'Switch between roles based on your current needs',
            'Use the unified dashboard for comprehensive management'
          ],
          quiz: [
            {
              question: 'How many specialized roles does your AI agent have?',
              options: ['3 roles', '5 roles', '7 roles', '10 roles'],
              correct: 1,
              explanation: 'Your AI agent has 5 specialized roles: Content Creator, Ad Manager, Tech Support, Customer Support, and Analytics.'
            }
          ]
        },
        {
          title: 'Content Creator Role',
          content: 'Master the AI Content Creator for generating viral posts and engaging content.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Content Creator Role',
          steps: [
            'Switch to Content Creator role in AI Agent',
            'Provide your business details and target audience',
            'Select content type and tone preferences',
            'Generate platform-optimized content',
            'Review and customize AI-generated content',
            'Schedule or post directly to social media'
          ],
          quiz: [
            {
              question: 'What can the Content Creator role do?',
              options: ['Only generate text', 'Generate posts, captions, hashtags, and post directly', 'Only schedule posts', 'Only create ads'],
              correct: 1,
              explanation: 'The Content Creator generates complete posts with captions, hashtags, and can post directly to social media.'
            }
          ]
        },
        {
          title: 'Ad Manager Role',
          content: 'Use the AI Ad Manager to create and optimize advertising campaigns.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Ad Manager Role',
          steps: [
            'Switch to Ad Manager role in AI Agent',
            'Select campaign objective and target audience',
            'Set budget and campaign duration',
            'Create ad copy and visuals from AI content',
            'Launch and monitor campaign performance',
            'Optimize campaigns based on AI insights'
          ],
          quiz: [
            {
              question: 'What\'s the recommended starting budget for AI-managed campaigns?',
              options: ['$5/day', '$25/day', '$100/day', '$500/day'],
              correct: 1,
              explanation: '$25/day allows enough data collection while keeping costs manageable for testing and optimization.'
            }
          ]
        },
        {
          title: 'Tech Support Role',
          content: 'Leverage the AI Tech Support for platform issues and integrations.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Tech Support Role',
          steps: [
            'Switch to Tech Support role when experiencing issues',
            'Describe your technical problem in detail',
            'Follow AI-guided troubleshooting steps',
            'Get step-by-step solutions for common issues',
            'Access platform integration help',
            'Receive guidance for account setup and maintenance'
          ],
          quiz: [
            {
              question: 'When should you use the Tech Support role?',
              options: ['Only for major issues', 'For any platform or integration problem', 'Never', 'Only for billing issues'],
              correct: 1,
              explanation: 'Use the Tech Support role for any platform, integration, or technical issue you encounter.'
            }
          ]
        },
        {
          title: 'Customer Support Role',
          content: 'Master the AI Customer Support for handling inquiries and engagement.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Customer Support Role',
          steps: [
            'Switch to Customer Support role for inquiries',
            'Get AI-generated responses to common questions',
            'Create professional customer service scripts',
            'Handle negative reviews and complaints',
            'Build customer relationship strategies',
            'Manage community engagement effectively'
          ],
          quiz: [
            {
              question: 'What can the Customer Support role help with?',
              options: ['Only positive reviews', 'All customer interactions including complaints', 'Only sales questions', 'Only technical issues'],
              correct: 1,
              explanation: 'The Customer Support role handles all customer interactions including complaints, reviews, and general inquiries.'
            }
          ]
        },
        {
          title: 'Analytics Role',
          content: 'Utilize the AI Analytics role for performance tracking and insights.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Analytics Role',
          steps: [
            'Switch to Analytics role for performance review',
            'Review key metrics and performance indicators',
            'Get AI-generated insights and recommendations',
            'Analyze competitor performance',
            'Create performance reports and forecasts',
            'Optimize strategies based on data insights'
          ],
          quiz: [
            {
              question: 'What insights can the Analytics role provide?',
              options: ['Only basic metrics', 'Comprehensive performance analysis and recommendations', 'Only follower count', 'Only engagement rate'],
              correct: 1,
              explanation: 'The Analytics role provides comprehensive performance analysis, competitor insights, and strategic recommendations.'
            }
          ]
        }
      ]
    },
    {
      id: 'trend-analysis',
      title: 'Trend Analysis & Market Intelligence',
      description: 'Master trend analysis and market intelligence features',
      duration: '12 min',
      difficulty: 'Intermediate',
      sections: [
        {
          title: 'Understanding Trend Analysis',
          content: 'Learn how to use trend analysis to stay ahead of your competition.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Trend Analysis Overview',
          steps: [
            'Navigate to Trend Analysis section',
            'Review trending topics in your industry',
            'Analyze competitor content strategies',
            'Identify emerging hashtags and topics',
            'Use trends to inform your content strategy'
          ],
          quiz: [
            {
              question: 'What is trend analysis in social media?',
              options: ['Only following trends', 'Analyzing trending topics, competitor strategies, and market intelligence', 'Just posting popular content', 'Only using hashtags'],
              correct: 1,
              explanation: 'Trend analysis involves analyzing trending topics, competitor strategies, and market intelligence to inform your content strategy.'
            }
          ]
        },
        {
          title: 'Market Intelligence Features',
          content: 'Master market intelligence tools to understand your industry landscape.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Market Intelligence',
          steps: [
            'Monitor industry trends and conversations',
            'Track competitor content and engagement',
            'Identify content gaps and opportunities',
            'Analyze audience sentiment and preferences',
            'Use insights to create relevant content'
          ],
          quiz: [
            {
              question: 'How can market intelligence help your content strategy?',
              options: ['It doesn\'t help', 'By identifying trends, gaps, and opportunities in your industry', 'Only by copying competitors', 'Only by using popular hashtags'],
              correct: 1,
              explanation: 'Market intelligence helps identify trends, content gaps, and opportunities to create more relevant and engaging content.'
            }
          ]
        }
      ]
    },
    {
      id: 'calendar-scheduling',
      title: 'Calendar & Scheduling Mastery',
      description: 'Master advanced content calendar and scheduling features',
      duration: '18 min',
      difficulty: 'Intermediate',
      sections: [
        {
          title: 'Understanding the Visual Calendar',
          content: 'Master the drag-and-drop calendar system for comprehensive content planning.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Calendar Overview',
          steps: [
            'Navigate to Calendar section in your dashboard',
            'Explore the visual drag-and-drop interface',
            'View multi-platform content in one calendar',
            'Organize content by categories and campaigns',
            'Enable team collaboration features'
          ],
          quiz: [
            {
              question: 'What makes our calendar system unique?',
              options: ['Only basic scheduling', 'Visual drag-and-drop interface with multi-platform view and team collaboration', 'Only single platform', 'Only manual entry'],
              correct: 1,
              explanation: 'Our calendar features a visual drag-and-drop interface, multi-platform view, and team collaboration capabilities for comprehensive content planning.'
            }
          ]
        },
        {
          title: 'Smart Scheduling & Optimization',
          content: 'Learn how to use AI-powered scheduling for optimal engagement times.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Smart Scheduling',
          steps: [
            'Understand AI-determined optimal posting times',
            'Configure platform-specific scheduling strategies',
            'Set up audience-based timing optimization',
            'Manage time zones for global audiences',
            'Enable automated publishing features'
          ],
          quiz: [
            {
              question: 'How does smart scheduling work?',
              options: ['Random timing', 'AI-determined optimal times based on audience analysis', 'Only manual scheduling', 'Only during business hours'],
              correct: 1,
              explanation: 'Smart scheduling uses AI to determine optimal posting times based on audience analysis and engagement patterns.'
            }
          ]
        },
        {
          title: 'Content Planning & Campaign Management',
          content: 'Master advanced content planning and campaign coordination features.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Content Planning',
          steps: [
            'Plan monthly content themes and campaigns',
            'Coordinate content with marketing campaigns',
            'Create seasonal and event-based content',
            'Set up recurring content series',
            'Monitor content distribution balance'
          ],
          quiz: [
            {
              question: 'What content planning features are available?',
              options: ['Only basic scheduling', 'Monthly themes, campaign coordination, seasonal content, and recurring series', 'Only daily posts', 'Only weekly posts'],
              correct: 1,
              explanation: 'Our platform supports monthly themes, campaign coordination, seasonal content, and recurring content series for comprehensive planning.'
            }
          ]
        },
        {
          title: 'Bulk Scheduling & Automation',
          content: 'Master bulk scheduling and automated content management.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Bulk Scheduling',
          steps: [
            'Schedule multiple posts simultaneously',
            'Set up recurring content patterns',
            'Configure automated publishing workflows',
            'Create content templates for efficiency',
            'Monitor scheduled content performance'
          ],
          quiz: [
            {
              question: 'What bulk scheduling features are available?',
              options: ['Only single posts', 'Multiple posts, recurring patterns, automated workflows, and content templates', 'Only manual scheduling', 'Only daily posts'],
              correct: 1,
              explanation: 'Bulk scheduling includes multiple posts, recurring patterns, automated workflows, and content templates for efficient content management.'
            }
          ]
        }
      ]
    },
    {
      id: 'ai-insights-analytics',
      title: 'AI Insights & Advanced Analytics',
      description: 'Master AI-powered analytics and predictive insights',
      duration: '20 min',
      difficulty: 'Advanced',
      sections: [
        {
          title: 'Understanding AI-Powered Analytics',
          content: 'Learn how AI analytics provide deep insights and predictive capabilities for your social media strategy.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'AI Analytics Overview',
          steps: [
            'Navigate to AI Insights section in your dashboard',
            'Understand real-time trend monitoring capabilities',
            'Explore growth prediction features',
            'Review sentiment analysis tools',
            'Learn about competitor intelligence features'
          ],
          quiz: [
            {
              question: 'What makes our analytics AI-powered?',
              options: ['Basic reporting only', 'Real-time trends, growth predictions, sentiment analysis, and competitor intelligence', 'Only manual data entry', 'Only historical data'],
              correct: 1,
              explanation: 'Our AI analytics include real-time trends, growth predictions, sentiment analysis, and competitor intelligence for comprehensive insights.'
            }
          ]
        },
        {
          title: 'Sentiment Analysis & Brand Monitoring',
          content: 'Master sentiment analysis for proactive brand management and reputation protection.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Sentiment Analysis',
          steps: [
            'Monitor brand mentions and sentiment trends',
            'Analyze customer feedback and sentiment patterns',
            'Set up crisis detection alerts',
            'Implement proactive reputation management',
            'Track sentiment changes over time'
          ],
          quiz: [
            {
              question: 'What does sentiment analysis help with?',
              options: ['Only positive reviews', 'Brand monitoring, customer feedback, crisis detection, and reputation management', 'Only negative comments', 'Only follower count'],
              correct: 1,
              explanation: 'Sentiment analysis helps with brand monitoring, customer feedback analysis, crisis detection, and proactive reputation management.'
            }
          ]
        },
        {
          title: 'Competitor Intelligence & Analysis',
          content: 'Learn how to analyze competitor strategies and identify growth opportunities.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Competitor Analysis',
          steps: [
            'Compare your performance metrics to competitors',
            'Analyze competitor content strategies and performance',
            'Identify content gaps and opportunities',
            'Track competitor engagement patterns',
            'Use insights to optimize your strategy'
          ],
          quiz: [
            {
              question: 'What competitor intelligence features are available?',
              options: ['Only basic metrics', 'Performance comparison, content analysis, gap identification, and engagement tracking', 'Only follower counts', 'Only post frequency'],
              correct: 1,
              explanation: 'Competitor intelligence includes performance comparison, content analysis, gap identification, and engagement pattern tracking.'
            }
          ]
        },
        {
          title: 'Predictive Analytics & Forecasting',
          content: 'Master predictive analytics for data-driven decision making.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Predictive Analytics',
          steps: [
            'Predict content performance before publishing',
            'Forecast audience growth trends',
            'Estimate lead generation and conversion rates',
            'Calculate campaign ROI predictions',
            'Use AI recommendations for optimization'
          ],
          quiz: [
            {
              question: 'What can predictive analytics forecast?',
              options: ['Only past performance', 'Content performance, audience growth, lead conversion, and campaign ROI', 'Only current metrics', 'Only basic trends'],
              correct: 1,
              explanation: 'Predictive analytics can forecast content performance, audience growth, lead conversion rates, and campaign ROI for strategic planning.'
            }
          ]
        },
        {
          title: 'AI Recommendations & Optimization',
          content: 'Learn how to use AI-generated recommendations to optimize your social media strategy.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'AI Recommendations',
          steps: [
            'Receive AI-generated content suggestions',
            'Get optimal posting time recommendations',
            'Use trending hashtag suggestions',
            'Implement audience targeting recommendations',
            'Apply AI insights to improve performance'
          ],
          quiz: [
            {
              question: 'What AI recommendations are available?',
              options: ['Only basic tips', 'Content suggestions, optimal timing, hashtag strategies, and audience targeting', 'Only manual suggestions', 'Only historical data'],
              correct: 1,
              explanation: 'AI recommendations include content suggestions, optimal posting times, hashtag strategies, and precise audience targeting for maximum engagement.'
            }
          ]
        }
      ]
    },
    {
      id: 'admin-dashboard',
      title: 'Admin Dashboard & Client Management',
      description: 'Master administrative features and client management',
      duration: '15 min',
      difficulty: 'Advanced',
      sections: [
        {
          title: 'Admin Dashboard Overview',
          content: 'Learn how to manage your platform as an administrator.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Admin Dashboard',
          steps: [
            'Access admin dashboard with admin credentials',
            'Review platform-wide analytics and metrics',
            'Manage client accounts and permissions',
            'Monitor system performance and usage',
            'Access advanced administrative tools'
          ],
          quiz: [
            {
              question: 'Who can access the admin dashboard?',
              options: ['All users', 'Only administrators', 'Only clients', 'Only developers'],
              correct: 1,
              explanation: 'Only administrators with proper credentials can access the admin dashboard and management tools.'
            }
          ]
        },
        {
          title: 'Client Management',
          content: 'Master client management features for multi-client operations.',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          videoTitle: 'Client Management',
          steps: [
            'Add and manage client accounts',
            'Set client permissions and access levels',
            'Monitor client usage and performance',
            'Generate client reports and analytics',
            'Provide client support and training'
          ],
          quiz: [
            {
              question: 'What can you do in client management?',
              options: ['Only view client names', 'Manage accounts, permissions, usage, and provide support', 'Only delete clients', 'Only change passwords'],
              correct: 1,
              explanation: 'Client management includes managing accounts, permissions, monitoring usage, and providing comprehensive support.'
            }
          ]
        }
      ]
    }
  ];

  const handleStepComplete = (stepIndex) => {
    setCompletedSteps([...completedSteps, stepIndex]);
    if (stepIndex === welcomeSteps.length - 1) {
      completeOnboarding();
    }
  };

  const completeOnboarding = () => {
    saveUserProgress({
      onboardingCompleted: true,
      trainingSessions: userProgress.trainingSessions + 1,
      lastTrainingDate: new Date().toISOString()
    });
    setShowWelcome(false);
    localStorage.setItem('trainingCompleted', 'true');
  };

  const skipOnboarding = () => {
    setShowWelcome(false);
    localStorage.setItem('trainingCompleted', 'true');
  };

  const startTraining = (module) => {
    setActiveTraining(module);
    setShowTrainingDialog(true);
    setCurrentSection(0); // Reset section progress when starting a new training
    setSectionProgress({});
  };

  const completeTraining = (moduleId) => {
    saveUserProgress({
      trainingSessions: userProgress.trainingSessions + 1,
      lastTrainingDate: new Date().toISOString(),
      featuresUsed: [...userProgress.featuresUsed, moduleId.split('-')[0]]
    });
    setShowTrainingDialog(false);
    setActiveTraining(null);
    setCurrentSection(0);
    setSectionProgress({});
  };

  const handleExitTraining = () => {
    setShowExitConfirm(true);
  };

  const confirmExitTraining = () => {
    setShowTrainingDialog(false);
    setActiveTraining(null);
    setCurrentSection(0);
    setSectionProgress({});
    setShowExitConfirm(false);
  };

  const cancelExitTraining = () => {
    setShowExitConfirm(false);
  };

  const completeSection = (sectionIndex) => {
    const newProgress = { ...sectionProgress };
    newProgress[sectionIndex] = true;
    setSectionProgress(newProgress);
    
    // Award points and badges for completion
    const pointsEarned = 50;
    const newBadges = [...userProgress.badges];
    
    // Award badges based on progress
    if (Object.keys(newProgress).length === 1) {
      newBadges.push('First Steps');
    }
    if (Object.keys(newProgress).length === 3) {
      newBadges.push('Halfway There');
    }
    if (Object.keys(newProgress).length === activeTraining?.sections?.length) {
      newBadges.push('Training Master');
    }
    
    saveUserProgress({
      totalPoints: userProgress.totalPoints + pointsEarned,
      trainingSessions: userProgress.trainingSessions + 1,
      badges: newBadges
    });
    
    // Show completion animation/notification
    // You could add a toast notification here
  };

  const getSkillLevel = () => {
    const sessions = userProgress.trainingSessions;
    if (sessions < 3) return 'Beginner';
    if (sessions < 8) return 'Intermediate';
    return 'Advanced';
  };

  const getTrainingProgress = () => {
    if (!activeTraining) return 0;
    const totalSections = activeTraining.sections?.length || 0;
    const completedSections = Object.keys(sectionProgress).length;
    return totalSections > 0 ? (completedSections / totalSections) * 100 : 0;
  };

  const getNextSection = () => {
    if (currentSection < (activeTraining?.sections?.length - 1)) {
      setCurrentSection(currentSection + 1);
    }
  };

  const getPrevSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Welcome Dialog for New Users */}
      <Dialog 
        open={showWelcome} 
        maxWidth="md" 
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <School color="primary" />
            <Typography variant="h5">
              Welcome to Your AI Social Media Manager
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={currentStep} orientation="vertical">
            {welcomeSteps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
                <StepContent>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      {step.content.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      {step.content.description}
                    </Typography>

                    {step.content.features && (
                      <List>
                        {step.content.features.map((feature, idx) => (
                          <ListItem key={idx} sx={{ px: 0 }}>
                            <ListItemIcon>
                              <CheckCircle color="success" />
                            </ListItemIcon>
                            <ListItemText primary={feature} />
                          </ListItem>
                        ))}
                      </List>
                    )}

                    {step.content.roles && (
                      <Grid container spacing={2}>
                        {step.content.roles.map((role, idx) => (
                          <Grid item xs={12} md={6} key={idx}>
                            <Card>
                              <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {role.icon}
                                  <Typography variant="h6">{role.title}</Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  {role.description}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    )}

                    {step.content.nextSteps && (
                      <Box>
                        <Typography variant="subtitle1" gutterBottom>
                          Next Steps:
                        </Typography>
                        <List>
                          {step.content.nextSteps.map((nextStep, idx) => (
                            <ListItem key={idx} sx={{ px: 0 }}>
                              <ListItemIcon>
                                <PlayArrow color="primary" />
                              </ListItemIcon>
                              <ListItemText primary={nextStep} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => handleStepComplete(index)}
                      sx={{ mr: 1 }}
                    >
                      {index === welcomeSteps.length - 1 ? 'Get Started' : 'Continue'}
                    </Button>
                    <Button
                      disabled={index === 0}
                      onClick={() => setCurrentStep(index - 1)}
                    >
                      Back
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </DialogContent>
        <DialogActions>
          <Button onClick={skipOnboarding} color="inherit">
            Skip Tutorial
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Training Dashboard */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            <School sx={{ mr: 1, verticalAlign: 'middle' }} />
            Training Center
          </Typography>
          <Chip 
            label={`${getSkillLevel()} Level`} 
            color="primary" 
            icon={<TrendingUp />}
          />
        </Box>

        {/* Progress Overview */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Your Learning Progress
              </Typography>
              <Chip 
                label={`${userProgress.totalPoints} Points`}
                color="warning"
                icon={<TrendingUp />}
              />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Typography variant="h4" color="primary">
                  {userProgress.trainingSessions}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Training Sessions Completed
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="h4" color="secondary">
                  {userProgress.featuresUsed.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Features Mastered
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="h4" color="success.main">
                  {Math.round((completedSteps.length / welcomeSteps.length) * 100)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Onboarding Complete
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="h4" color="info.main">
                  {suggestedTraining.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Suggested Trainings
                </Typography>
              </Grid>
            </Grid>
            
            {/* Badges */}
            {userProgress.badges.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  🏆 Your Badges
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {userProgress.badges.map((badge, index) => (
                    <Chip
                      key={index}
                      label={badge}
                      color="success"
                      size="small"
                      icon={<AutoAwesome />}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Suggested Training */}
        {suggestedTraining.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <AutoAwesome sx={{ mr: 1, verticalAlign: 'middle' }} />
                AI-Recommended Training
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Based on your usage patterns, here are personalized training recommendations:
              </Typography>
              <Grid container spacing={2}>
                {suggestedTraining.map((training) => (
                  <Grid item xs={12} md={6} lg={4} key={training.id}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { boxShadow: 4 }
                      }}
                      onClick={() => startTraining(training)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          {training.icon}
                          <Typography variant="h6">{training.title}</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {training.description}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Chip 
                            label={training.priority} 
                            color={training.priority === 'high' ? 'error' : 'warning'} 
                            size="small"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {training.estimatedTime}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Available Training Modules */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Available Training Modules
            </Typography>
            <Grid container spacing={2}>
              {trainingModules.map((module) => (
                <Grid item xs={12} md={6} lg={4} key={module.id}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => startTraining(module)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6" color="primary">
                          {module.title}
                        </Typography>
                        <Chip 
                          label="+50 pts" 
                          size="small" 
                          color="warning"
                          sx={{ ml: 'auto' }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {module.description}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Chip 
                          label={module.difficulty} 
                          size="small" 
                          color={module.difficulty === 'Beginner' ? 'success' : module.difficulty === 'Intermediate' ? 'warning' : 'error'}
                        />
                        <Typography variant="caption" color="text.secondary">
                          ⏱️ {module.duration}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Chip label={`${module.sections?.length} sections`} size="small" variant="outlined" />
                        <Chip label="Interactive" size="small" variant="outlined" color="primary" />
                      </Box>
                      <Button
                        variant="contained"
                        startIcon={<PlayArrow />}
                        fullWidth
                        sx={{ 
                          background: 'linear-gradient(45deg, #00d4aa 30%, #0099ff 90%)',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #00b894 30%, #0077cc 90%)'
                          }
                        }}
                      >
                        Start Training
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Training Dialog */}
      <Dialog 
        open={showTrainingDialog} 
        maxWidth="lg" 
        fullWidth
        onClose={handleExitTraining}
        disableEscapeKeyDown
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h5">
                {activeTraining?.title}
              </Typography>
              <Chip 
                label={`${Math.round(getTrainingProgress())}% Complete`}
                color="success"
                size="small"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleExitTraining}
                startIcon={<Close />}
              >
                Exit
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {activeTraining && (
            <Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {activeTraining.description}
              </Typography>
              
              {/* Progress Bar */}
              <Box sx={{ mb: 3 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={getTrainingProgress()} 
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Section {currentSection + 1} of {activeTraining.sections?.length}
                </Typography>
              </Box>
              
              {/* Current Section */}
              {activeTraining.sections?.[currentSection] && (
                <Box sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    {activeTraining.sections[currentSection].title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {activeTraining.sections[currentSection].content}
                  </Typography>
                  
                  {/* Video Section */}
                  {activeTraining.sections[currentSection].videoUrl && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        📹 {activeTraining.sections[currentSection].videoTitle}
                      </Typography>
                      <Box sx={{ position: 'relative', width: '100%', height: 200, bgcolor: 'grey.100', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Video: {activeTraining.sections[currentSection].videoTitle}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {/* Steps Section */}
                  {activeTraining.sections[currentSection].steps && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        📋 Step-by-Step Guide
                      </Typography>
                      <List dense>
                        {activeTraining.sections[currentSection].steps.map((step, stepIndex) => (
                          <ListItem key={stepIndex} sx={{ px: 0 }}>
                            <ListItemIcon>
                              <Typography variant="body2" color="primary" fontWeight="bold">
                                {stepIndex + 1}.
                              </Typography>
                            </ListItemIcon>
                            <ListItemText primary={step} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  
                  {/* Quiz Section */}
                  {activeTraining.sections[currentSection].quiz && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        🧠 Knowledge Check
                      </Typography>
                      {activeTraining.sections[currentSection].quiz.map((quizItem, quizIndex) => (
                        <Box key={quizIndex} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight="bold" gutterBottom>
                            {quizItem.question}
                          </Typography>
                          <List dense>
                            {quizItem.options.map((option, optionIndex) => (
                              <ListItem key={optionIndex} sx={{ px: 0 }}>
                                <ListItemIcon>
                                  <Typography variant="body2" color="text.secondary">
                                    {String.fromCharCode(65 + optionIndex)}.
                                  </Typography>
                                </ListItemIcon>
                                <ListItemText primary={option} />
                              </ListItem>
                            ))}
                          </List>
                          <Box sx={{ mt: 1, p: 1, bgcolor: 'primary.main', color: 'white', borderRadius: 1 }}>
                            <Typography variant="caption">
                              💡 {quizItem.explanation}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button size="small" startIcon={<VideoLibrary />} variant="outlined">
                      Watch Video
                    </Button>
                    <Button size="small" startIcon={<Quiz />} variant="outlined">
                      Take Quiz
                    </Button>
                    <Button 
                      size="small" 
                      startIcon={<CheckCircle />} 
                      variant="contained"
                      onClick={() => completeSection(currentSection)}
                      disabled={sectionProgress[currentSection]}
                    >
                      {sectionProgress[currentSection] ? 'Completed' : 'Mark Complete'}
                    </Button>
                  </Box>
                </Box>
              )}
              
              {/* Navigation */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={getPrevSection}
                  disabled={currentSection === 0}
                  startIcon={<ExpandMore sx={{ transform: 'rotate(90deg)' }} />}
                >
                  Previous
                </Button>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {activeTraining.sections?.map((_, index) => (
                    <Box
                      key={index}
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: index === currentSection ? 'primary.main' : 
                                sectionProgress[index] ? 'success.main' : 'grey.300',
                        cursor: 'pointer'
                      }}
                      onClick={() => setCurrentSection(index)}
                    />
                  ))}
                </Box>
                
                <Button
                  variant="contained"
                  onClick={getNextSection}
                  disabled={currentSection === (activeTraining.sections?.length - 1)}
                  endIcon={<ExpandMore sx={{ transform: 'rotate(-90deg)' }} />}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleExitTraining} color="inherit">
            Exit Training
          </Button>
          <Button 
            variant="contained" 
            onClick={() => completeTraining(activeTraining?.id)}
            disabled={getTrainingProgress() < 100}
            startIcon={<CheckCircle />}
          >
            Complete Training
          </Button>
        </DialogActions>
      </Dialog>

      {/* Exit Confirmation Dialog */}
      <Dialog
        open={showExitConfirm}
        maxWidth="sm"
        fullWidth
        onClose={cancelExitTraining}
      >
        <DialogTitle>
          <Typography variant="h6">
            Exit Training?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your progress will be saved, but you'll need to complete all sections to finish the training.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Progress: {Math.round(getTrainingProgress())}% complete
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelExitTraining}>
            Continue Training
          </Button>
          <Button onClick={confirmExitTraining} variant="contained" color="error">
            Exit Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingSystem; 